// Updated ClientDashboard.tsx with Toast integration
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientDashboardProps, Pet, DashboardData } from '../../types';
import { Dog, Cat, Bird } from 'lucide-react';
import { clearTOTPSession } from '../../utils/totpSession';
import Toast from './Toast';
import ConfirmationModal from '../common/ConfirmationModal';
import LoadingSpinner from '../common/LoadingSpinner';
import QuestionnaireModal from '../models/QuestionnaireModel';
import { petService } from '../../api/petService';
import { riskScoreService } from '../../api/riskScoreService';
import { residentService } from '../../api/residentService';
import { settingsService } from '../../api/settingsService';
import PetList from '../dashboard/PetList';
import UpcomingCharges from '../dashboard/UpcomingCharges';
import DocumentDetails from '../details/documentDetails';
import PetDetailsModal from '../details/petDetails';
import PetQuestion from '../petQuestion';
import PetCount from '../petCount';
import MultiPetStart from '../multiPetStart';
import NoPet from '../noPet';
import type { NoPetDeclarationData } from '../noPet';
import AlreadyRegisteredNoPets from '../AlreadyRegisteredNoPets';
import AttestationBox from '../common/AttestationBox';
import StripePayment from '../payments/StripePayment';
import { useAnnouncement } from '../../hooks/useAnnouncement';
import {
  photoOutcomeFromRegistration,
  petPhotoUploadErrorMessage,
  type PhotoUploadOutcome,
} from '../../utils/petPhotoUpload';
import { registrationToastMessage } from '../../utils/registrationError';

const ClientDashboard: React.FC<ClientDashboardProps> = ({ appData, user }) => {
  const navigate = useNavigate();
  const [showQuestionnaire, setShowQuestionnaire] = useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [registering, setRegistering] = useState<boolean>(false); // Separate state for pet registration
  const [showDocumentDetails, setShowDocumentDetails] = useState<boolean>(false);
  const [showPetDetails, setShowPetDetails] = useState<boolean>(false);
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [showPetQuestion, setShowPetQuestion] = useState<boolean>(false);
  const [hasAnsweredPetQuestion, setHasAnsweredPetQuestion] = useState<boolean>(false);
  const [showPetCount, setShowPetCount] = useState<boolean>(false);
  const [showMultiPetStart, setShowMultiPetStart] = useState<boolean>(false);
  const [selectedPetCount, setSelectedPetCount] = useState<number>(1);
  const [showNoPetScreen, setShowNoPetScreen] = useState<boolean>(false);
  const [showAlreadyRegisteredNoPets, setShowAlreadyRegisteredNoPets] = useState<boolean>(false);
  const [dismissedNoPetsScreen, setDismissedNoPetsScreen] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>(null);
  const [multiPetData, setMultiPetData] = useState<(Pet | null)[]>([]);
  const [currentPetIndex, setCurrentPetIndex] = useState<number>(0);
  const [completedPets, setCompletedPets] = useState<Set<number>>(new Set());
  const [completedPetIds, setCompletedPetIds] = useState<Map<number, number>>(new Map());
  const [isMultiPetWorkflow, setIsMultiPetWorkflow] = useState<boolean>(false);
  const [registeredPetsInSession, setRegisteredPetsInSession] = useState<any[]>([]); // Track newly registered pets
  const [residentInfo, setResidentInfo] = useState<any>(null); // Store full resident info including property/community
  const [localPetImages, setLocalPetImages] = useState<Map<number, string>>(new Map()); // Map of pet_id (integer) -> local blob URL
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    onConfirm?: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'warning' | 'danger' | 'info';
  }>({});
  const [petStartToken, setPetStartToken] = useState<number>(0); // bump to start a fresh pet form
  const [draftPet, setDraftPet] = useState<Pet | null>(null);
  const [draftedPets, setDraftedPets] = useState<Map<number, { id: number; data: any }>>(new Map());
  const [deletingDraftId, setDeletingDraftId] = useState<number | null>(null);
  const [showPayment, setShowPayment] = useState<boolean>(false);
  const [propertySettings, setPropertySettings] = useState<any>(null); // Settings from properties.extra_data
  // Per-lease pet-cap snapshot from /api/pets/lease-summary. Source of truth
  // for remaining slots — counts pets across every co-resident on the
  // lease, not just the current user.
  const [leasePetSummary, setLeasePetSummary] = useState<{
    lease_id: number | null;
    partition: 'lease' | 'user';
    active_pet_count: number;
    max_allowed: number;
    remaining: number;
  } | null>(null);
  const [unitExclusion, setUnitExclusion] = useState<{ reason: string } | null>(null); // If the resident's unit is excluded
  const [exclusionCheckDone, setExclusionCheckDone] = useState(false); // Don't render dashboard until exclusion is verified
  const [exclusionAcknowledged, setExclusionAcknowledged] = useState(false);
  const [highlightExclusionCheckbox, setHighlightExclusionCheckbox] = useState(false);
  const [paymentPet, setPaymentPet] = useState<{ id: number; name: string } | null>(null);
  const [paymentAutoStart, setPaymentAutoStart] = useState<boolean>(false);
  const [redirectingToStripe, setRedirectingToStripe] = useState<boolean>(false);
  const [refreshingDashboard, setRefreshingDashboard] = useState<boolean>(false);

  // Toast notification state
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isVisible: false,
    message: '',
    type: 'success'
  });

  const currentUser = user || null;
  const { announce } = useAnnouncement();
  const paymentReturnHandled = useRef(false);
  const dashboardFetchedOnce = useRef(false);
  const dashboardFetchInFlight = useRef(false);
  const dashboardFetchPromise = useRef<Promise<void> | null>(null);

  const saveMultiPetStateForStripe = () => {
    if (!isMultiPetWorkflow) return;
    const state = {
      isMultiPetWorkflow: true,
      selectedPetCount,
      multiPetData,
      completedPets: Array.from(completedPets),
      completedPetIds: Object.fromEntries(completedPetIds),
      draftedPets: Object.fromEntries(draftedPets),
    };
    try {
      sessionStorage.setItem('multiPetWorkflowState', JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save multi-pet workflow state:', e);
    }
  };

  // Function to get user's display name with fallbacks
  const getUserDisplayName = () => {
    // Try userData first (from dashboard API)
    if (userData?.displayName) {
      return userData.displayName;
    }
    
    // Try currentUser
    if (currentUser?.displayName) {
      return currentUser.displayName;
    }
    
    // Try to extract name from email
    if (currentUser?.email) {
      const emailName = currentUser.email.split('@')[0];
      // Capitalize first letter and replace dots/underscores with spaces
      return emailName
        .replace(/[._]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    // Final fallback
    return "User";
  };

  // Fetch resident info ONCE on mount to check is_primary status and get property info
  const residentInfoFetched = useRef(false);
  useEffect(() => {
    console.log('🔄 Resident info useEffect - currentUser:', currentUser?.email);

    if (currentUser && !residentInfoFetched.current) {
      residentInfoFetched.current = true;
      const userEmail = currentUser.email;
      console.log('🚀 Fetching resident info for email:', userEmail);

      if (userEmail) {
        residentService.getResidentInfoByEmail(userEmail)
          .then(info => {
            console.log('📦 RAW resident info response:', JSON.stringify(info, null, 2));
            if (info) {
              console.log('👤 Resident info loaded successfully');
              console.log('🏢 Property/Community:', info.community, '(ID:', info.community_id, ')');
              console.log('📍 Unit:', info.unit);
              setResidentInfo(info);
            } else {
              console.warn('⚠️ Resident info is null/undefined');
            }
          })
          .catch(err => {
            console.error('❌ ERROR fetching resident info:', err);
          });
      }
    }
  }, [currentUser]);

  // Fetch property settings when resident info is available
  useEffect(() => {
    if (!residentInfo) return;
    if (!residentInfo.community) {
      // No community — no settings to fetch, no exclusion possible
      setExclusionCheckDone(true);
      return;
    }
    const lookup = residentInfo.community_id || residentInfo.community;
    settingsService.getPropertySettings(String(lookup))
      .then(settings => {
        console.log('⚙️ Property settings loaded:', settings);
        setPropertySettings(settings);
      })
      .catch(err => {
        console.warn('⚠️ Could not fetch property settings:', err);
        setExclusionCheckDone(true); // Settings fetch failed — allow dashboard to render
      });
  }, [residentInfo?.community]);

  // Check if the resident's unit is excluded (match unit + building)
  useEffect(() => {
    if (!propertySettings) return;

    // If no exclusions or no unit info, mark check done and move on
    if (!propertySettings.unitExclusions?.length || !residentInfo?.unit) {
      setExclusionCheckDone(true);
      return;
    }

    const exclusions = propertySettings.unitExclusions as Array<{ unit: string; building: string; isExcluded: boolean; reason: string }>;
    const residentUnit = String(residentInfo.unit).trim().toLowerCase();
    const residentBuilding = residentInfo.building ? String(residentInfo.building).trim().toLowerCase() : '';

    console.log('🔍 Exclusion check — resident unit:', residentInfo.unit, '| building:', residentInfo.building);
    console.log('🔍 Exclusion entries:', exclusions);

    const match = exclusions.find(e => {
      if (!e.isExcluded) return false;
      const unitMatch = String(e.unit).trim().toLowerCase() === residentUnit;
      if (!unitMatch) return false;
      // If both sides have a building value, compare (contains match, case-insensitive)
      if (e.building && residentBuilding) {
        const exclBuilding = String(e.building).trim().toLowerCase();
        return residentBuilding.includes(exclBuilding) || exclBuilding.includes(residentBuilding);
      }
      return true;
    });
    if (match) {
      setUnitExclusion({ reason: match.reason });
    }
    setExclusionCheckDone(true);
  }, [propertySettings, residentInfo]);

  useEffect(() => {
    // In React StrictMode (dev), effects run twice on mount.
    // Guard so we don't double-fetch and thrash loading state.
    if (currentUser && !dashboardFetchedOnce.current) {
      dashboardFetchedOnce.current = true;
      fetchDashboardData({ showSpinner: true });
    }
  }, [currentUser]);

  const fetchDashboardData = async (opts?: { showSpinner?: boolean }) => {
    const showSpinner = opts?.showSpinner ?? false;
    if (dashboardFetchInFlight.current) {
      if (dashboardFetchPromise.current) await dashboardFetchPromise.current;
      return;
    }
    const doFetch = async () => {
      try {
        dashboardFetchInFlight.current = true;
        if (showSpinner) setLoading(true);
        const data = await petService.getDashboardData();
        if (import.meta.env.DEV) {
          console.log('📊 Dashboard: pets=', data?.pets?.length || 0);
        }
        setDashboardData(data);
        setUserData(data.user);

        // Refresh the per-lease cap snapshot alongside the dashboard so the
        // "remaining slots" math stays in sync with what the backend will
        // accept on the next register call. Failure here is non-fatal —
        // the dashboard still renders; we just fall back to legacy math.
        petService
          .getLeasePetSummary()
          .then((summary) => setLeasePetSummary(summary))
          .catch((err) => console.warn('Lease pet summary fetch failed:', err));

        // has_pets: true by default until user completes no-pet declaration; then false
        const userDeclaredNoPets = data.user?.has_pets === false;
        const hasPetRecords = (data.user?.petIds?.length ?? 0) > 0;

        if (userDeclaredNoPets && !hasPetRecords && !dismissedNoPetsScreen) {
          setShowAlreadyRegisteredNoPets(true);
          setShowPetQuestion(false);
        } else {
          setShowAlreadyRegisteredNoPets(false);
          const showQuestion = !hasPetRecords && !hasAnsweredPetQuestion && !isMultiPetWorkflow;
          setShowPetQuestion(showQuestion);
        }

      } catch (err: any) {
        console.error('Dashboard fetch error:', err);
      } finally {
        dashboardFetchInFlight.current = false;
        dashboardFetchPromise.current = null;
        if (showSpinner) setLoading(false);
      }
    };
    dashboardFetchPromise.current = doFetch();
    await dashboardFetchPromise.current;
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
    // Also announce to screen readers
    announce(message, type === 'error' ? 'assertive' : 'polite');
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const registrationToastForPhotoOutcome = (
    photoOutcome: PhotoUploadOutcome,
    baseMessage: string,
    petName: string,
    defaultType: 'success' | 'info' = 'info',
  ): { message: string; type: 'success' | 'error' | 'info' | 'warning' } => {
    if (photoOutcome === 'success') {
      return {
        message: `${baseMessage} Pet photo uploaded successfully.`,
        type: defaultType,
      };
    }
    if (photoOutcome === 'failed' || photoOutcome === 'missing_required') {
      const name = petName?.trim() || 'your pet';
      return {
        message: `${baseMessage} However, the photo for ${name} was not saved — please upload it from your dashboard.`,
        type: 'warning',
      };
    }
    return { message: baseMessage, type: defaultType };
  };

  // Handle Stripe Checkout return (success/cancel) via query params
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (paymentReturnHandled.current) return;

    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (!payment) return;

    paymentReturnHandled.current = true;

    // Restore multi-pet workflow state saved before Stripe redirect
    const savedMultiPetState = sessionStorage.getItem('multiPetWorkflowState');
    if (savedMultiPetState) {
      try {
        const state = JSON.parse(savedMultiPetState);
        if (state.isMultiPetWorkflow) {
          setIsMultiPetWorkflow(true);
          setSelectedPetCount(state.selectedPetCount || 2);
          setMultiPetData(state.multiPetData || new Array(state.selectedPetCount || 2).fill(null));
          setCompletedPets(new Set(state.completedPets || []));
          setCompletedPetIds(new Map(Object.entries(state.completedPetIds || {}).map(([k, v]) => [Number(k), v as number])));
          setDraftedPets(new Map(Object.entries(state.draftedPets || {}).map(([k, v]) => [Number(k), v as { id: number; data: any }])));
          setShowMultiPetStart(true);
        }
      } catch (e) {
        console.error('Failed to restore multi-pet workflow state:', e);
      }
      sessionStorage.removeItem('multiPetWorkflowState');
    }

    if (payment === 'success') {
      const sessionId = params.get('session_id');
      const petId = params.get('pet_id');

      showToast('Payment received! Confirming your registration…', 'info');

      if (sessionId && petId) {
        const pId = Number(petId);
        const token = localStorage.getItem('authToken');
        const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost'
          ? 'http://localhost:8000/api/'
          : (import.meta.env.VITE_API_URL);
        fetch(`${apiBase}stripe/verify-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ session_id: sessionId, pet_id: petId }),
        })
          .then(res => res.json())
          .then((data) => {
            fetchDashboardData({ showSpinner: false });
            if (data.status === 'paid') {
              petService.sendPaymentConfirmationEmail(pId)
                .catch(err => console.error('Confirmation email trigger failed:', err))
                .finally(() => showToast('Thank you! Your registration is complete.', 'success'));
            } else {
              showToast('Thank you! Your registration is complete.', 'success');
            }
          })
          .catch(err => {
            console.error('Session verification failed, falling back to dashboard refresh:', err);
            fetchDashboardData({ showSpinner: false });
            showToast('Thank you! Your registration is complete.', 'success');
          });
      } else {
        fetchDashboardData({ showSpinner: false }).catch(err => console.error('Dashboard refresh after payment failed:', err));
        showToast('Thank you! Your registration is complete.', 'success');
      }
    } else if (payment === 'cancelled') {
      showToast('Payment cancelled. You can complete it later from your dashboard.', 'info');
      fetchDashboardData({ showSpinner: false }).catch(err => console.error('Dashboard refresh after payment cancel failed:', err));
    }

    params.delete('payment');
    params.delete('pet_id');
    params.delete('session_id');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, []);

  // Auto-save unsaved multi-pet data as drafts on page close / refresh.
  // beforeunload only shows the confirmation dialog; pagehide sends the beacon
  // so that cancelling the refresh does NOT create a duplicate draft.
  useEffect(() => {
    if (!isMultiPetWorkflow) return;

    const getUnsavedPets = () =>
      multiPetData
        .map((pet, i) => ({ pet, index: i }))
        .filter(({ pet, index }) => pet?.petName && !completedPets.has(index) && !draftedPets.has(index));

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (getUnsavedPets().length === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return;
      const unsaved = getUnsavedPets();
      if (unsaved.length === 0) return;

      for (const { pet } of unsaved) {
        if (!pet) continue;
        petService.saveDraftBeacon({
          property_info: {
            property: pet.property || '',
            unit: pet.unit || '',
            contact_email: pet.contact_email || '',
            contact_phone: pet.contact_phone || '',
          },
          pet_details: {
            pet_name: pet.petName || pet.pet_name || '',
            pet_type: pet.petType || pet.pet_type || 'dog',
            breed: pet.breed || '',
            age: pet.age || 0,
            weight: typeof pet.weight === 'object' ? (pet.weight.value || 0) : (parseFloat(String(pet.weight)) || 0),
            color: pet.color || null,
            gender: pet.gender || null,
          },
          veterinary_info: {
            vet_clinic: pet.vetClinic || pet.vet_clinic || '',
            vet_phone: pet.vetPhone || pet.vet_phone || '',
            vet_address: pet.vetAddress || pet.vet_address || null,
          },
          esa_info: {
            pet_role: pet.animalType || pet.pet_role || 'pet',
          },
          custom_fields: pet.customFields || pet.custom_fields || {},
          draft_step: 1,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isMultiPetWorkflow, multiPetData, completedPets, draftedPets]);

  const handleSinglePetComplete = async (formData: any) => {
    try {
      // If user previously declared no pets, update has_pets to true
      if (userData?.has_pets === false) {
        try { await petService.setHasPets(); } catch (err) { console.error('Failed to update has_pets:', err); }
      }

      // Determine if payment will be needed before closing questionnaire
      const firstPetPrecheck = formData.pets && formData.pets[0];
      const preAnimalType = firstPetPrecheck?.animalType || 'pet';
      const willNeedPayment = preAnimalType !== 'esa' && preAnimalType !== 'sa';

      // Close questionnaire and show loading overlay
      setShowQuestionnaire(false);
      setDraftPet(null);
      if (willNeedPayment) {
        setRedirectingToStripe(true);
      } else {
        setRefreshingDashboard(true);
      }
      
      const registeredPet = await petService.registerPets(formData);
      const registerResult = registeredPet?.results?.[0];
      const newPetId = registerResult?.petId;
      
      // Reset workflow state
      setIsMultiPetWorkflow(false);
      setSelectedPetCount(1);
      setMultiPetData([]);
      setCompletedPets(new Set());
      setCompletedPetIds(new Map());
      
      const firstPet = formData.pets && formData.pets[0];
      const photoOutcome: PhotoUploadOutcome = firstPet
        ? photoOutcomeFromRegistration(firstPet, propertySettings, registerResult)
        : 'none';
      if (photoOutcome === 'missing_required' || photoOutcome === 'failed') {
        showToast(petPhotoUploadErrorMessage(firstPet?.petName || 'your pet'), 'error');
      }

      setRedirectingToStripe(false);
      setRefreshingDashboard(false);

      const photoBlocked = photoOutcome === 'missing_required' || photoOutcome === 'failed';

      // Show success message and trigger payment (skip Stripe for ESA/SA animals)
      if (newPetId && photoBlocked) {
        setRefreshingDashboard(true);
        try {
          await fetchDashboardData({ showSpinner: false });
        } finally {
          setRefreshingDashboard(false);
        }
      } else if (newPetId) {
        const petAnimalType = firstPet?.animalType || 'pet';
        const isFeeExempt = petAnimalType === 'esa' || petAnimalType === 'sa';

        if (isFeeExempt) {
          setRefreshingDashboard(true);
          try {
            await fetchDashboardData({ showSpinner: false });
          } finally {
            setRefreshingDashboard(false);
          }
          const label = petAnimalType === 'esa' ? 'emotional support animal' : 'service animal';
          const base = `Your ${label} has been registered successfully! Fee exemption applied. Pending admin review.`;
          const toastPayload = registrationToastForPhotoOutcome(
            photoOutcome,
            base,
            firstPet?.petName || 'Your pet',
            'success',
          );
          showToast(toastPayload.message, toastPayload.type);
        } else {
          const base = 'Registration submitted! Please complete payment to finalize.';
          const toastPayload = registrationToastForPhotoOutcome(
            photoOutcome,
            base,
            firstPet?.petName || 'Your pet',
            'info',
          );
          showToast(toastPayload.message, toastPayload.type);
          setPaymentAutoStart(true);
          setPaymentPet({ id: newPetId, name: firstPet?.petName || 'Your pet' });
          setShowPayment(true);
          fetchDashboardData({ showSpinner: false }).catch(err => console.error('Failed to refresh dashboard:', err));
        }
      } else {
        setRedirectingToStripe(false);
        showToast("Your pet has been added!", 'success');
      }
      
      if (newPetId) {
        riskScoreService.getRiskScore(newPetId)
          .then(() => {
            console.log('✅ Risk score calculated in background');
          })
          .catch((e) => {
            console.error('Risk assessment failed in background:', e);
          });
      }
    } catch (error: any) {
      setRedirectingToStripe(false);
      setRefreshingDashboard(false);
      showToast(registrationToastMessage(error), 'error');
      console.error('Registration error:', error);
    }
  };
  // Handle individual pet completion in multi-pet workflow
  const handleIndividualPetComplete = async (petData: any, petIndex: number) => {
    try {
      // If user previously declared no pets, update has_pets to true
      if (userData?.has_pets === false) {
        try { await petService.setHasPets(); } catch (err) { console.error('Failed to update has_pets:', err); }
      }

      console.log('Registering pet:', petIndex, petData.petName);
      console.log('📸 Pet data received:', {
        petName: petData.petName,
        hasPhotoFile: !!petData?.photoFile,
        photoFile: petData?.photoFile,
        photoFileName: petData?.photoFile?.name,
        photoFileSize: petData?.photoFile?.size
      });
      
      // Determine if payment will be needed
      const preAnimalType = petData.pet_role || petData.animalType || 'pet';
      const willNeedPayment = preAnimalType !== 'esa' && preAnimalType !== 'sa';

      // Close questionnaire and show loading overlay
      setShowQuestionnaire(false);
      setDraftPet(null);
      setShowMultiPetStart(true);
      if (willNeedPayment) {
        setRedirectingToStripe(true);
      } else {
        setRefreshingDashboard(true);
      }
      
      // If this pet was previously saved as a draft, pass the draft ID so
      // the backend updates the existing record instead of creating a new one.
      const draftEntry = draftedPets.get(petIndex);
      const formDataForSubmission: any = {
        property: petData.property,
        unit: petData.unit,
        contactEmail: petData.contactEmail,
        contactPhone: petData.contactPhone,
        ...(draftEntry ? { draftPetId: draftEntry.id } : {}),
        pets: [{
          petName: petData.petName,
          petType: petData.petType,
          breed: petData.breed,
          age: petData.age,
          gender: petData.gender,
          move_in_date: petData.move_in_date,
          move_out_date: petData.move_out_date,
          spayed_neutered: petData.spayed_neutered,
          weight: petData.weight,
          color: petData.color,
          markings: petData.markings,
          animalType: petData.pet_role || petData.animalType || 'pet',
          vetClinic: petData.vetClinic,
          vetPhone: petData.vetPhone,
          vetAddress: petData.vetAddress,
          lastVisit: petData.lastVisit,
          esaProfessional: petData.esaProfessional,
          esaLetterDate: petData.esaLetterDate,
          esaRegistration: petData.esaRegistration,
          documents: petData.documents || [],
          photos: petData.photos || [],
          customFields: petData.customFields || {}
        }]
      };
      
      // Register this pet with the backend
      const registeredPet = await petService.registerPets(formDataForSubmission);
      const registerResult = registeredPet?.results?.[0];
      const newPetId = registerResult?.petId;
      
      console.log('Pet registered successfully:', registeredPet);

      // Update multi-pet data array with the completed pet data
      const updatedPetData = [...multiPetData];
      updatedPetData[petIndex] = petData;
      setMultiPetData(updatedPetData);
      
      // Track this newly registered pet
      setRegisteredPetsInSession(prev => [...prev, registeredPet]);
      
      // Mark this pet as completed and clear from drafted if applicable
      const updatedCompletedPets = new Set(completedPets);
      updatedCompletedPets.add(petIndex);
      setCompletedPets(updatedCompletedPets);
      if (newPetId) {
        setCompletedPetIds(prev => new Map(prev).set(petIndex, newPetId));
      }
      setDraftedPets(prev => {
        if (!prev.has(petIndex)) return prev;
        const updated = new Map(prev);
        updated.delete(petIndex);
        return updated;
      });

      const photoOutcome: PhotoUploadOutcome = photoOutcomeFromRegistration(
        petData,
        propertySettings,
        registerResult,
      );
      if (photoOutcome === 'missing_required' || photoOutcome === 'failed') {
        showToast(petPhotoUploadErrorMessage(petData.petName || 'your pet'), 'error');
      }

      setRedirectingToStripe(false);
      setRefreshingDashboard(false);

      const photoBlocked = photoOutcome === 'missing_required' || photoOutcome === 'failed';

      // Show success message and trigger payment (skip Stripe for ESA/SA animals)
      if (newPetId && photoBlocked) {
        setRefreshingDashboard(true);
        try {
          await fetchDashboardData({ showSpinner: false });
        } finally {
          setRefreshingDashboard(false);
        }
      } else if (newPetId) {
        const petAnimalType = petData.pet_role || petData.animalType || 'pet';
        const isFeeExempt = petAnimalType === 'esa' || petAnimalType === 'sa';

        if (isFeeExempt) {
          setRefreshingDashboard(true);
          try {
            await fetchDashboardData({ showSpinner: false });
          } finally {
            setRefreshingDashboard(false);
          }
          const label = petAnimalType === 'esa' ? 'emotional support animal' : 'service animal';
          const base = `${petData.petName} has been registered as a ${label} successfully! Fee exemption applied. Pending admin review.`;
          const toastPayload = registrationToastForPhotoOutcome(
            photoOutcome,
            base,
            petData.petName || 'Your pet',
            'success',
          );
          showToast(toastPayload.message, toastPayload.type);
        } else {
          const base = `${petData.petName} has been registered successfully!`;
          const toastPayload = registrationToastForPhotoOutcome(
            photoOutcome,
            base,
            petData.petName || 'Your pet',
            'success',
          );
          showToast(toastPayload.message, toastPayload.type);
          setPaymentAutoStart(true);
          setPaymentPet({ id: newPetId, name: petData.petName || 'Your pet' });
          setShowPayment(true);
          fetchDashboardData({ showSpinner: false }).catch(err => console.error('Failed to refresh dashboard:', err));
        }
      } else {
        setRedirectingToStripe(false);
        showToast(`${petData.petName} has been registered successfully!`, 'success');
      }
      
      if (newPetId) {
        riskScoreService.getRiskScore(newPetId)
          .then(() => {
            console.log('✅ Risk score calculated in background');
          })
          .catch((e) => {
            console.error('Risk assessment failed in background:', e);
          });
      }
    } catch (error: any) {
      setRedirectingToStripe(false);
      setRefreshingDashboard(false);
      showToast(registrationToastMessage(error), 'error');
      console.error('Individual pet registration error:', error);
    }
  };
  // Handle pet question modal responses
  const handlePetQuestionClose = () => {
    setShowPetQuestion(false);
    setHasAnsweredPetQuestion(true);
    // Only track in session state, not localStorage or database
    // This allows the question to show again on next login if user still has 0 pets
  };

  const handleHasPets = () => {
    setShowPetQuestion(false);
    setHasAnsweredPetQuestion(true);
    setShowPetCount(true);
  };
  const handleNoPets = () => {
    setShowPetQuestion(false);
    setHasAnsweredPetQuestion(true);
    setShowNoPetScreen(true);
  };
  // Handle pet count selection
  const handlePetCountContinue = (count: number) => {
    setShowPetCount(false);
    setSelectedPetCount(count);
    if (count === 1) {
      // Single pet - go directly to questionnaire
      setIsMultiPetWorkflow(false);
      setShowQuestionnaire(true);
    } else {
      // Multiple pets - initialize multi-pet workflow
      setIsMultiPetWorkflow(true);
      setMultiPetData(new Array(count).fill(null));
      setCompletedPets(new Set());
      setCompletedPetIds(new Map());
      setDraftedPets(new Map());
      setRegisteredPetsInSession([]);
      setShowMultiPetStart(true);
    }
  };
  // Handle starting individual pet registration from multi-pet interface
  const handleStartPetRegistration = async (petIndex: number) => {
    console.log('Starting pet registration for pet index:', petIndex);

    // If the pet is already completed, open the edit/details modal instead
    if (completedPets.has(petIndex)) {
      const petId = completedPetIds.get(petIndex);
      const petInDashboard = petId
        ? dashboardData?.pets?.find(p => (p.id || p.pet_id) === petId)
        : null;
      if (petInDashboard) {
        setSelectedPet(petInDashboard);
        setShowPetDetails(true);
        return;
      }
    }

    setCurrentPetIndex(petIndex);
    setPetStartToken((t) => t + 1);

    const drafted = draftedPets.get(petIndex);
    if (drafted) {
      try {
        const fullPet = await petService.getPetDetails(drafted.id);
        setDraftPet(fullPet);
      } catch {
        setDraftPet(drafted.data as Pet);
      }
    } else {
      setDraftPet(null);
    }

    setShowMultiPetStart(false);
    setShowQuestionnaire(true);
  };
  // Handle completing all pets in multi-pet workflow (when user clicks Complete All)
  const handleMultiPetComplete = async () => {
    setShowMultiPetStart(false);
    sessionStorage.removeItem('multiPetWorkflowState');
    await fetchDashboardData({ showSpinner: true });
    showToast(`All ${selectedPetCount} pets have been registered successfully!`, 'success');
    setIsMultiPetWorkflow(false);
    setSelectedPetCount(1);
    setMultiPetData([]);
    setCompletedPets(new Set());
    setCompletedPetIds(new Map());
    setDraftedPets(new Map());
    setCurrentPetIndex(0);
    setRegisteredPetsInSession([]);
  };
  // Handle closing multi-pet start modal
  const handleMultiPetStartClose = () => {
    // User clicked X button - ask for confirmation if there are incomplete pets
    const incompletePets = selectedPetCount - completedPets.size;
    if (incompletePets > 0) {
      const savedDrafts = draftedPets.size;
      const unsavedCount = incompletePets - savedDrafts;
      const parts: string[] = [];
      if (savedDrafts > 0) parts.push(`${savedDrafts} saved as draft${savedDrafts > 1 ? 's' : ''}`);
      if (unsavedCount > 0) parts.push(`${unsavedCount} not yet started`);
      const detail = parts.join(' and ');

      setConfirmationConfig({
        title: "Exit Registration?",
        message: savedDrafts > 0 && unsavedCount === 0
          ? `You have ${detail}. Drafts can be resumed from your dashboard. Are you sure you want to exit?`
          : `You still have ${detail}. Unsaved progress will be lost. Are you sure you want to exit?`,
        confirmText: "Yes, Exit",
        cancelText: "Continue Registration",
        type: savedDrafts > 0 && unsavedCount === 0 ? "warning" : "danger",
        onConfirm: () => {
          setShowConfirmation(false);
          setShowMultiPetStart(false);
          sessionStorage.removeItem('multiPetWorkflowState');
          setIsMultiPetWorkflow(false);
          setSelectedPetCount(1);
          setMultiPetData([]);
          setCompletedPets(new Set());
          setCompletedPetIds(new Map());
          setDraftedPets(new Map());
          setCurrentPetIndex(0);
          setRegisteredPetsInSession([]);
        }
      });
      setShowConfirmation(true);
      return;
    }
    setShowMultiPetStart(false);
    sessionStorage.removeItem('multiPetWorkflowState');
    setIsMultiPetWorkflow(false);
    setSelectedPetCount(1);
    setMultiPetData([]);
    setCompletedPets(new Set());
    setCompletedPetIds(new Map());
    setDraftedPets(new Map());
    setCurrentPetIndex(0);
    setRegisteredPetsInSession([]);
  };
  // Handle closing the NoPet screen
  const handleNoPetClose = () => {
    setShowNoPetScreen(false);
  };

  // No-pet declaration: set has_pets to false on backend (NoPet then calls onClose)
  const handleNoPetDeclare = async (data: NoPetDeclarationData) => {
    try {
      await petService.submitNoPetDeclaration({
        phone: data.phone,
        attestation: data.attestation,
      });
      // Sync local state with DB so Stay Logged In shows AlreadyRegisteredNoPets
      // instead of a stale has_pets=true dashboard with Add Pet.
      await fetchDashboardData({ showSpinner: false });
    } catch (err: any) {
      console.error('Failed to submit no-pet declaration', err);
      const message =
        err?.response?.data?.detail ||
        (err?.response?.status === 401 ? 'Please sign in again.' : 'Failed to save your declaration. Please try again.');
      showToast(typeof message === 'string' ? message : 'Failed to save your declaration. Please try again.', 'error');
      throw err;
    }
  };
  // Handle Add Pet button click — any resident can register, no primary check.
  const handleAddPetClick = () => {
    setShowPetCount(true);
  };
  // Handle closing pet count modal
  const handlePetCountClose = () => {
    setShowPetCount(false);
  };
  const handleViewPetDetails = (pet: Pet) => {
    const fullPetData = dashboardData?.pets?.find(apiPet => apiPet.id === pet.id);
    setSelectedPet(fullPetData || pet);
    setShowPetDetails(true);
  };

  const handleResumeDraft = async (pet: Pet) => {
    const petId = Number(pet.id ?? pet.pet_id);
    try {
      if (petId) {
        const fullPet = await petService.getPetDetails(petId);
        setDraftPet(fullPet);
      } else {
        setDraftPet(pet);
      }
    } catch {
      setDraftPet(pet);
    }
    setIsMultiPetWorkflow(false);
    setSelectedPetCount(1);
    setShowQuestionnaire(true);
  };

  const handleDeleteDraft = (pet: Pet) => {
    const petId = Number(pet.id ?? pet.pet_id);
    const petName = pet.name || pet.petName || 'this draft';
    setConfirmationConfig({
      title: 'Delete Draft?',
      message: `Are you sure you want to delete the draft for "${petName}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Keep',
      type: 'danger',
      onConfirm: async () => {
        setShowConfirmation(false);
        setDeletingDraftId(petId);
        try {
          await petService.deletePet(petId);
          await fetchDashboardData({ showSpinner: false });
          showToast('Draft deleted.', 'success');
        } catch (err) {
          console.error('Failed to delete draft:', err);
          showToast('Failed to delete draft. Please try again.', 'error');
        } finally {
          setDeletingDraftId(null);
        }
      },
    });
    setShowConfirmation(true);
  };

  const getDocumentIcon = (docType: string) => {
    switch (docType) {
      case 'vaccination':
        return '💉';
      case 'insurance':
        return '🏥';
      case 'license':
        return '📋';
      case 'esa-letter':
        return '📃';
      default:
        return '📄';
    }
  };



  // Transform API data for display - ensure we're getting all pet data
  const myPets = dashboardData?.pets?.map(pet => ({
    id: pet.id || pet.pet_id || '',
    pet_id: pet.pet_id,
    petName: pet.pet_name || pet.name || '',
    pet_name: pet.pet_name,
    name: pet.pet_name || pet.name,
    breed: pet.breed || '',
    age: pet.age || 0,
    date_of_birth: (pet as any).date_of_birth,
    petType: pet.pet_type || pet.petType || 'dog',
    pet_type: pet.pet_type,
    weight: pet.weight || 0,
    color: pet.color,
    gender: pet.gender,
    move_in_date: pet.move_in_date,
    move_out_date: pet.move_out_date,
    spayed_neutered: pet.spayed_neutered,
    vetClinic: (pet as any).vet_clinic || '',
    vetPhone: (pet as any).vet_phone || '',
    vetAddress: (pet as any).vet_address,
    lastVisit: (pet as any).last_visit,
    pet_role: (pet as any).pet_role || 'pet',
    animalType: (pet as any).pet_role || 'pet',
    esaProfessional: (pet as any).esa_professional,
    esaLetterDate: (pet as any).esa_letter_date,
    esaRegistration: (pet as any).esa_registration,
    contact_email: (pet as any).contact_email,
    contact_phone: (pet as any).contact_phone,
    documents: pet.documents || [],
    status: pet.status || 'Active',
    registered_date: pet.registered_date,
    created_at: pet.created_at,
    image_url: pet.image_url,
    stripe_payment_status: (pet as any).stripe_payment_status,
    deactivated_reason: (pet as any).deactivated_reason,
    deactivation_notes: (pet as any).deactivation_notes,
    deactivated_by: (pet as any).deactivated_by,
    deactivated_at: (pet as any).deactivated_at,
    icon: (pet.pet_type === 'dog' || pet.petType === 'dog') ? Dog :
      (pet.pet_type === 'cat' || pet.petType === 'cat') ? Cat : Bird,
    registeredDate: pet.registered_date || pet.created_at || new Date().toISOString()
  } as Pet & { icon: React.ComponentType; registeredDate: string })) || [];

  const dashboardDraftCount =
    dashboardData?.pets?.filter((p) => (p.status ?? '').toLowerCase() === 'drafted').length ?? 0;

  const upcomingCharges = dashboardData?.upcoming_charges?.map(charge => ({
    id: charge.id,
    description: charge.description,
    amount: charge.amount,
    dueDate: new Date(charge.due_date).toLocaleDateString()
  })) || [];

  const metrics = dashboardData?.metrics || {
    registered_pets: 0,
    compliance_status: 'Pending',
    monthly_charges: 0
  };

  const recentDocuments = dashboardData?.recent_documents || [];

  if (loading || !exclusionCheckDone) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <LoadingSpinner size="large" text="Loading your dashboard..." />
      </div>
    );
  }

  // Unit exclusion: resident's unit is excluded from pet registration
  if (unitExclusion) {
    const handleExclusionLogout = () => {
      localStorage.removeItem('authToken');
      clearTOTPSession();
      sessionStorage.removeItem('sessionActive');
      navigate('/login', { replace: true });
    };

    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="w-full h-full bg-gray-50 flex items-center justify-center overflow-y-auto p-4">
          <div className="flex flex-col items-center space-y-6 px-4 max-w-md w-full">
            {/* Brand logo */}
            <div className="flex items-center">
              <img
                src="/landing/getpawtected.svg"
                alt="GetPawtected Logo"
                className="h-12 sm:h-16 w-auto drop-shadow-sm"
              />
            </div>

            {/* Heading */}
            <div className="text-center space-y-2">
              <p
                className="text-xl sm:text-2xl text-black font-medium"
                style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.05em' }}
              >
                Unit Exclusion Notice
              </p>
            </div>

            {/* Exclusion reason attestation box */}
            <div className="w-full max-w-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Reason for Exclusion</h3>
              <AttestationBox
                text={unitExclusion.reason}
                checked={exclusionAcknowledged}
                onCheckedChange={(checked) => {
                  setExclusionAcknowledged(checked);
                  if (checked) setHighlightExclusionCheckbox(false);
                }}
                label="I acknowledge this notice."
                id="unit-exclusion-attestation"
                required
                highlight={highlightExclusionCheckbox}
              />
            </div>

            {/* Log Out button */}
            <div className="w-full max-w-sm pt-2">
              <button
                type="button"
                onClick={() => {
                  if (!exclusionAcknowledged) {
                    setHighlightExclusionCheckbox(true);
                    return;
                  }
                  handleExclusionLogout();
                }}
                className="relative w-full min-h-[48px] px-6 py-3 rounded-full font-semibold overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 pmc-btn-primary hover:opacity-95"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Log Out
              </button>
              {highlightExclusionCheckbox && !exclusionAcknowledged && (
                <p className="text-xs text-red-500 text-center mt-2">
                  Please check the box above to acknowledge
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No-pet flows: NoPet = first-time declaration + attestation; AlreadyRegisteredNoPets = returning user (has_pets false)
  if (showNoPetScreen) {
    const prefilledPhone = userData?.phone || (dashboardData?.user as any)?.phone || '';
    return (
      <NoPet
        onClose={handleNoPetClose}
        initialPhone={prefilledPhone}
        onNoPetDeclare={handleNoPetDeclare}
      />
    );
  }

  // Users who previously declared no pets: show dedicated page (popup) with option to register a pet
  if (showAlreadyRegisteredNoPets) {
    return (
      <AlreadyRegisteredNoPets
        onRegisterPet={() => {
          // has_pets flips to true only when pet registration actually starts
          // (handleSinglePetComplete / multi-pet flow), not on this button.
          setDismissedNoPetsScreen(true);
          setShowAlreadyRegisteredNoPets(false);
          setShowPetCount(true);
        }}
      />
    );
  }

  return (
  <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
    {/* Toast Notification */}
    <Toast
      message={toast.message}
      type={toast.type}
      isVisible={toast.isVisible}
      onClose={hideToast}
    />

    {/* Top Header - Fixed */}
    <header className="bg-white border-b border-gray-200 h-[73px] flex items-center flex-shrink-0" role="banner">
      <div className="px-4 sm:px-6 w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Overview</h1>
          </div>
          <div className="flex items-center gap-2">
          </div>
        </div>
      </div>
    </header>

    {/* Main Content - Scrollable */}
    <main id="main-content" className="flex-1 overflow-y-auto min-h-0" role="main">
      <div className="p-4 sm:p-5">
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-5">
          {/* Alert Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start sm:items-center gap-3" role="status" aria-live="polite">
            <div className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5 sm:mt-0" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-xs sm:text-sm text-gray-900 flex-1">
              Welcome {getUserDisplayName()}! You can use this portal to manage your animal registrations and documents.
            </span>
          </div>

          {/* Cards Container */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            <PetList 
              myPets={myPets}
              registeredPetsInSession={registeredPetsInSession}
              onAddPetClick={handleAddPetClick}
              onViewPetDetails={handleViewPetDetails}
              onResumeDraft={handleResumeDraft}
              onDeleteDraft={handleDeleteDraft}
              deletingDraftId={deletingDraftId}
              onPayRegistrationFee={(pet) => {
                const id = Number(pet.id ?? pet.pet_id);
                if (!id) {
                  showToast('Unable to start payment: missing pet id.', 'error');
                  return;
                }
                setPaymentAutoStart(false);
                setPaymentPet({ id, name: pet.name || pet.petName || 'Your pet' });
                setShowPayment(true);
              }}
              isLoading={loading}
              localPetImages={localPetImages}
            />
          </div>
        </div>
      </div>
    </main>

    {/* Modals - Keep as is */}
    <PetQuestion
      isOpen={showPetQuestion}
      onClose={handlePetQuestionClose}
      onHasPets={handleHasPets}
      onNoPets={handleNoPets}
    />
    <PetCount
      isOpen={showPetCount}
      onClose={handlePetCountClose}
      onContinue={handlePetCountContinue}
      propertyName={residentInfo?.community}
      // Source of truth: the lease summary from the backend, which counts
      // pets across every co-resident on the lease. Fall back to the
      // per-user math only while the first summary fetch is in-flight.
      maxPetsAllowed={
        leasePetSummary?.max_allowed
          ?? propertySettings?.general?.maxPetsPerUnit
          ?? 5
      }
      remainingSlots={
        leasePetSummary
          ? leasePetSummary.remaining
          : (propertySettings?.general?.maxPetsPerUnit ?? 5)
            - (dashboardData?.pets?.filter(p => p.status !== 'Inactive' && p.status !== 'Rejected')?.length || 0)
      }
      draftCount={dashboardDraftCount}
    />
    <MultiPetStart
      isOpen={showMultiPetStart}
      onClose={handleMultiPetStartClose}
      petCount={selectedPetCount}
      onStartPet={handleStartPetRegistration}
      onComplete={handleMultiPetComplete}
      petsData={multiPetData as any}
      completedPets={completedPets}
      draftedPets={draftedPets}
      isRegistering={registering}
      dashboardPets={myPets}
      completedPetIds={completedPetIds}
      onPayRegistrationFee={(pet) => {
        const id = Number((pet as any).id ?? (pet as any).pet_id);
        if (!id) {
          showToast('Unable to start payment: missing pet id.', 'error');
          return;
        }
        setPaymentAutoStart(false);
        setPaymentPet({ id, name: (pet as any).name || (pet as any).petName || 'Your pet' });
        setShowPayment(true);
      }}
      onSaveDraft={async () => {
        setRefreshingDashboard(true);
        try {
          const incompletePets = multiPetData
            .map((pet, i) => ({ pet, index: i }))
            .filter(({ pet, index }) => pet?.petName && !completedPets.has(index) && !draftedPets.has(index));
          for (const { pet } of incompletePets) {
            if (!pet) continue;
            await petService.saveDraft({
              property_info: {
                property: pet.property || '',
                unit: pet.unit || '',
                contact_email: pet.contact_email || '',
                contact_phone: pet.contact_phone || '',
              },
              pet_details: {
                pet_name: pet.petName || pet.pet_name || '',
                pet_type: pet.petType || pet.pet_type || 'dog',
                breed: pet.breed || '',
                age: pet.age || 0,
                weight: typeof pet.weight === 'object' ? (pet.weight.value || 0) : (parseFloat(String(pet.weight)) || 0),
                color: pet.color || null,
                gender: pet.gender || null,
              },
              veterinary_info: {
                vet_clinic: pet.vetClinic || pet.vet_clinic || '',
                vet_phone: pet.vetPhone || pet.vet_phone || '',
                vet_address: pet.vetAddress || pet.vet_address || null,
              },
              esa_info: {
                pet_role: pet.animalType || pet.pet_role || 'pet',
              },
              custom_fields: pet.customFields || pet.custom_fields || {},
              draft_step: 1,
            });
          }
          setShowMultiPetStart(false);
          setIsMultiPetWorkflow(false);
          setSelectedPetCount(1);
          setMultiPetData([]);
          setCompletedPets(new Set());
          setCompletedPetIds(new Map());
          setDraftedPets(new Map());
          setCurrentPetIndex(0);
          setRegisteredPetsInSession([]);
          await fetchDashboardData({ showSpinner: false });
        } finally {
          setRefreshingDashboard(false);
        }
        showToast('Your progress has been saved. You can resume drafts from the dashboard.', 'info');
      }}
      onFinishWithDrafts={async () => {
        setRefreshingDashboard(true);
        try {
          setShowMultiPetStart(false);
          setIsMultiPetWorkflow(false);
          setSelectedPetCount(1);
          setMultiPetData([]);
          setCompletedPets(new Set());
          setCompletedPetIds(new Map());
          setDraftedPets(new Map());
          setCurrentPetIndex(0);
          setRegisteredPetsInSession([]);
          await fetchDashboardData({ showSpinner: false });
        } finally {
          setRefreshingDashboard(false);
        }
        showToast('Your progress has been saved. You can resume drafts from the dashboard.', 'info');
      }}
    />
    <QuestionnaireModal
      isOpen={showQuestionnaire}
      onClose={() => {
        setShowQuestionnaire(false);
        setDraftPet(null);
        if (isMultiPetWorkflow) {
          setShowMultiPetStart(true);
        }
      }}
      onComplete={isMultiPetWorkflow ? undefined : handleSinglePetComplete}
      onPetComplete={isMultiPetWorkflow ? handleIndividualPetComplete : undefined}
      isMultiPetWorkflow={isMultiPetWorkflow}
      currentPetIndex={currentPetIndex}
      petStartToken={petStartToken}
      petQuantity={selectedPetCount}
      userProperty={residentInfo ? { id: residentInfo.community_id, name: residentInfo.community } as any : null}
      leasePetSummary={leasePetSummary}
      dashboardDraftCount={dashboardDraftCount}
      {...{ draftPet, onDraftSaved: async (draftPetId?: number) => {
        if (isMultiPetWorkflow && draftPetId) {
          const petData = {
            ...(multiPetData[currentPetIndex] || {}),
            id: draftPetId,
            pet_id: draftPetId,
          };
          setDraftedPets(prev => new Map(prev).set(currentPetIndex, { id: draftPetId, data: petData }));
          setShowMultiPetStart(true);
          setDraftPet(null);
          await fetchDashboardData({ showSpinner: false });
        } else {
          if (isMultiPetWorkflow) {
            setDraftedPets(prev => {
              const next = new Map(prev);
              next.delete(currentPetIndex);
              return next;
            });
            setShowMultiPetStart(true);
          }
          setDraftPet(null);
          setRefreshingDashboard(true);
          try {
            await fetchDashboardData({ showSpinner: false });
          } finally {
            setRefreshingDashboard(false);
          }
        }
      }} as any}
    />

    <PetDetailsModal
      isOpen={showPetDetails}
      onClose={() => {
        setShowPetDetails(false);
        if (isMultiPetWorkflow) {
          setShowMultiPetStart(true);
        }
      }}
      petData={selectedPet}
      propertyData={dashboardData?.property}
      propertySettings={propertySettings}
      showToast={showToast}
      onPetUpdated={async (updatedPet: Pet) => {
        const data = await petService.getDashboardData();
        setDashboardData(data);
        
        if (updatedPet && data?.pets) {
          const freshPet = data.pets.find(p => p.id === updatedPet.id);
          if (freshPet) {
            setSelectedPet({
              ...freshPet,
              id: freshPet.id || freshPet.pet_id,
              name: freshPet.pet_name || freshPet.name,
            });
          }
        }
        showToast('Pet updated successfully!', 'success');
      }}
      onPetDeleted={async (petId: string) => {
        await fetchDashboardData({ showSpinner: false });
        showToast('Pet deleted successfully!', 'success');
      }}
    />

    <DocumentDetails
      isOpen={showDocumentDetails}
      onClose={() => setShowDocumentDetails(false)}
      onDocumentUpdate={() => {
        fetchDashboardData({ showSpinner: false });
      }}
    />

    <ConfirmationModal
      isOpen={showConfirmation}
      onClose={() => setShowConfirmation(false)}
      onConfirm={confirmationConfig.onConfirm || (() => {})}
      title={confirmationConfig.title || 'Confirm Action'}
      message={confirmationConfig.message || 'Are you sure?'}
      confirmText={confirmationConfig.confirmText}
      cancelText={confirmationConfig.cancelText}
      type={confirmationConfig.type}
    />

    {paymentPet && (
      <StripePayment
        isOpen={showPayment}
        onClose={async () => {
          setShowPayment(false);
          setPaymentPet(null);
          setPaymentAutoStart(false);
          setRedirectingToStripe(false);
          setRefreshingDashboard(true);
          try {
            await fetchDashboardData({ showSpinner: false });
          } finally {
            setRefreshingDashboard(false);
          }
        }}
        onPaymentSuccess={async () => {
          setShowPayment(false);
          setPaymentPet(null);
          setPaymentAutoStart(false);
          setRedirectingToStripe(false);
          setRefreshingDashboard(true);
          try {
            await fetchDashboardData({ showSpinner: false });
          } finally {
            setRefreshingDashboard(false);
          }
          showToast('Thank you! Your registration is complete.', 'success');
        }}
        onRedirectStart={() => {
          saveMultiPetStateForStripe();
          setRedirectingToStripe(true);
        }}
        petId={paymentPet.id}
        petName={paymentPet.name}
        autoStart={paymentAutoStart}
      />
    )}

    {redirectingToStripe && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-sm">
        <LoadingSpinner size="large" text="Redirecting to secure checkout…" />
      </div>
    )}

    {refreshingDashboard && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-sm">
        <LoadingSpinner size="large" text="Updating your dashboard…" />
      </div>
    )}
  </div>
);
};

export default ClientDashboard;